<template>
  <section class="py-12 md:py-16 lg:py-20 statistics-section relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50">
    
    <div class="absolute inset-0 overflow-hidden">
      <svg class="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="circuitPattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
            <path d="M50,0 L50,100 M0,50 L100,50" stroke="rgba(59, 130, 246, 0.3)" stroke-width="0.5" fill="none"/>
            <circle cx="50" cy="50" r="3" fill="rgba(59, 130, 246, 0.5)"/>
            <circle cx="20" cy="20" r="2" fill="rgba(99, 102, 241, 0.3)"/>
            <circle cx="80" cy="80" r="2" fill="rgba(99, 102, 241, 0.3)"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#circuitPattern)"/>
      </svg>

      <div class="absolute top-10 left-10 w-20 h-20 border-2 border-blue-200/50 rounded-lg transform rotate-45 animate-pulse"></div>
      <div class="absolute bottom-20 right-10 w-16 h-16 border-2 border-blue-300/50 rounded-full animate-ping" style="animation-duration: 3s;"></div>
      <div class="absolute top-1/3 right-1/4 w-32 h-32 border border-blue-100/50 rounded-full animate-spin" style="animation-duration: 20s;"></div>
      
      <div class="absolute top-0 left-0 right-0 h-20 overflow-hidden opacity-10">
        <div class="binary-stream whitespace-nowrap text-blue-400 font-mono text-sm animate-marquee">
          01010101 01010011 01000101 01010010 00100000 01000110 01010010 01001001 01000101 01001110 01000100 01001100 01011001
        </div>
      </div>
    </div>

    <div class="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div class="text-center mb-2 md:mb-1">
        <div class="inline-flex items-center gap-3 mb-4">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            </svg>
          </div>
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
            {{ $t('statistics.title') }} <span class="text-blue-600">{{ $t('statistics.titleHighlight') }}</span>
          </h2>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-6xl mx-auto">
        <div class="group">
          <div class="stat-card cyber-card">
            <div class="relative z-10">
              <div class="flex items-start justify-between mb-4">
                <div class="icon-wrapper bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-200">
                  <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                </div>
                <div class="trend-badge">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                  </svg>
                  <span class="text-xs font-semibold">+12%</span>
                </div>
              </div>
              
              <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">{{ $t('statistics.receivedLabel') }}</h3>
              <div class="flex items-baseline gap-2 mb-4">
                <span class="text-3xl md:text-4xl font-bold text-gray-900 counter" data-target="1200">0</span>
                <span class="text-blue-600 font-semibold">+</span>
              </div>
              
              <div class="stat-bar bg-gray-200 rounded-full h-2 overflow-hidden">
                <div class="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full" style="width: 85%"></div>
              </div>
              <p class="text-sm text-gray-500 mt-2">{{ $t('statistics.receivedActivity') }}</p>
            </div>
          </div>
        </div>

        <div class="group">
          <div class="stat-card cyber-card">
            <div class="relative z-10">
              <div class="flex items-start justify-between mb-4">
                <div class="icon-wrapper bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 border border-emerald-200">
                  <svg class="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div class="trend-badge success">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                  </svg>
                  <span class="text-xs font-semibold">75%</span>
                </div>
              </div>
              
              <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">{{ $t('statistics.mitigatedLabel') }}</h3>
              <div class="flex items-baseline gap-2 mb-4">
                <span class="text-3xl md:text-4xl font-bold text-gray-900 counter" data-target="900">0</span>
                <span class="text-emerald-600 font-semibold">+</span>
              </div>
              
              <div class="stat-bar bg-gray-200 rounded-full h-2 overflow-hidden">
                <div class="bg-gradient-to-r from-emerald-500 to-emerald-600 h-full rounded-full" style="width: 75%"></div>
              </div>
              <p class="text-sm text-gray-500 mt-2">{{ $t('statistics.mitigatedActivity') }}</p>
            </div>
          </div>
        </div>

        <div class="group">
          <div class="stat-card cyber-card">
            <div class="relative z-10">
              <div class="flex items-start justify-between mb-4">
                <div class="icon-wrapper bg-gradient-to-br from-amber-500/10 to-amber-600/10 border border-amber-200">
                  <svg class="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div class="trend-badge warning">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                  </svg>
                  <span class="text-xs font-semibold">{{ $t('statistics.pendingBadge') }}</span>
                </div>
              </div>
              
              <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">{{ $t('statistics.pendingLabel') }}</h3>
              <div class="flex items-baseline gap-2 mb-4">
                <span class="text-3xl md:text-4xl font-bold text-gray-900 counter" data-target="300">0</span>
                <span class="text-amber-600 font-semibold">+</span>
              </div>
              
              <div class="stat-bar bg-gray-200 rounded-full h-2 overflow-hidden">
                <div class="bg-gradient-to-r from-amber-500 to-amber-600 h-full rounded-full" style="width: 25%"></div>
              </div>
              <p class="text-sm text-gray-500 mt-2">{{ $t('statistics.pendingActivity') }}</p>
            </div>
          </div>
        </div>

        <div class="group">
          <div class="stat-card cyber-card">
            <div class="relative z-10">
              <div class="flex items-start justify-between mb-4">
                <div class="icon-wrapper bg-gradient-to-br from-violet-500/10 to-violet-600/10 border border-violet-200">
                  <svg class="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                  </svg>
                </div>
                <div class="trend-badge success">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                  </svg>
                  <span class="text-xs font-semibold">+8%</span>
                </div>
              </div>
              
              <h3 class="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">{{ $t('statistics.phonesLabel') }}</h3>
              <div class="flex items-baseline gap-2 mb-4">
                <span class="text-3xl md:text-4xl font-bold text-gray-900 counter" data-target="150">0</span>
                <span class="text-violet-600 font-semibold">+</span>
              </div>
              
              <div class="stat-bar bg-gray-200 rounded-full h-2 overflow-hidden">
                <div class="bg-gradient-to-r from-violet-500 to-violet-600 h-full rounded-full" style="width: 40%"></div>
              </div>
              <p class="text-sm text-gray-500 mt-2">{{ $t('statistics.phonesActivity') }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-12 md:mt-16 max-w-2xl mx-auto">
        <div class="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-sm p-4 md:p-6">
          <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div class="flex items-center gap-3">
              <div class="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span class="font-semibold text-gray-900">{{ $t('statistics.uptime') }}: <span class="text-green-600">99.9%</span></span>
            </div>
            <div class="flex items-center gap-6 text-sm text-gray-600">
              <div class="flex items-center gap-2">
                <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span>{{ $t('statistics.availability') }}</span>
              </div>
              <div class="hidden sm:block w-px h-6 bg-gray-300"></div>
              <div class="flex items-center gap-2">
                <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span>{{ $t('statistics.responseTime') }} <span class="font-semibold">{{ $t('statistics.responseValue') }}</span></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'StatisticsSection',
  mounted() {
    this.animateCounters();
    this.observeSection();
  },
  methods: {
    animateCounters() {
      const counters = document.querySelectorAll('.counter');
      
      counters.forEach(counter => {
        const target = parseInt(counter.getAttribute('data-target'));
        const duration = 2000;
        const increment = target / (duration / 16);
        let current = 0;
        
        const updateCounter = () => {
          current += increment;
          if (current < target) {
            counter.textContent = Math.floor(current).toLocaleString();
            requestAnimationFrame(updateCounter);
          } else {
            counter.textContent = target.toLocaleString();
          }
        };
        
        updateCounter();
      });
    },
    
    observeSection() {
      const section = document.querySelector('.statistics-section');
      const cards = document.querySelectorAll('.stat-card');
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            cards.forEach((card, i) => {
              setTimeout(() => {
                card.classList.add('animate-in');
              }, i * 150);
            });
          }
        });
      }, { threshold: 0.1 });
      
      if (section) {
        observer.observe(section);
      }
    }
  }
};
</script>

<style scoped>
/* Animations */
@keyframes marquee {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.animate-marquee {
  animation: marquee 20s linear infinite;
}

/* Stat Card */
.stat-card {
  position: relative;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.stat-card.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.cyber-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 1.5rem;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.cyber-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.05), transparent);
  transition: left 0.7s ease;
}

.cyber-card:hover::before {
  left: 100%;
}

.cyber-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border-color: #d1d5db;
}

.icon-wrapper {
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.cyber-card:hover .icon-wrapper {
  transform: scale(1.1) rotate(5deg);
}

.trend-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 9999px;
  color: #2563eb;
}

.trend-badge.success {
  background: rgba(16, 185, 129, 0.1);
  border-color: rgba(16, 185, 129, 0.2);
  color: #059669;
}

.trend-badge.warning {
  background: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.2);
  color: #d97706;
}

/* Stat Bar */
.stat-bar {
  position: relative;
  overflow: hidden;
}

.stat-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

@media (max-width: 640px) {
  .stat-card {
    padding: 1.25rem;
  }
  
  .icon-wrapper {
    width: 2.5rem;
    height: 2.5rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .animate-marquee,
  .stat-card,
  .cyber-card::before,
  .icon-wrapper,
  .stat-bar::after {
    animation: none;
    transition: none;
  }
  
  .cyber-card:hover {
    transform: none;
  }
}
</style>